import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { LeftNavLinkNavigationEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { Messages } from '~src/codegen/Localize';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { showDMAwareness } from '~src/commonInfra/components/DMAwareness/DmAwarenessUtils';
import { Utils } from '~src/ntbue/Utils';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import { ConnectedLinkEnabledSitePicker } from '~src/siteManagement/components/layout/ConnectedLinkEnabledSitePicker';

import { ILogo, LogoService } from '../../../settings/general-settings/components/sections/siteLogo/services/LogoService';
import { ConnectedSitePicker } from '../../../siteManagement/components/layout/ConnectedSitePicker';
import { SpaceType } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { ContentLinks, ServerLinks, SiteLinks } from './NavLinkDefinitions';
import { NavList, NavListItem, NavListItemRenderProp, NavListItemState, NavListProps } from './NavList';
import { NavLocation, NavLocationAreas } from './NavLocation';
import { NavPanelLogo } from './NavPanelLogo';
import { NavSectionSeparator } from './NavSectionSeparator';
import isRecycleBinEnabled = RecycleBinUtils.isRecycleBinEnabled;
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { ISiteCountAndCloudAdminStatus } from '~src/siteManagement/serverApi/types/ISiteCountAndCloudAdminStatus';

export type NavPanelDisplayMode = 'icon' | 'iconAndText';

export interface NavPanelOwnProps {
  readonly navPanelDisplayMode: NavPanelDisplayMode;
  readonly serverUrls: ServerUrls;
  readonly onSitePickerClick?: () => void;
  readonly onSelectionMade?: () => void;
}

export interface NavPanelStateProps {
  readonly siteUrls: SpacesSiteUrls;
  readonly spaceType: SpaceType;
  readonly currentNavLocation: NavLocation;
  readonly inServerArea: boolean;

  readonly isSessionSiteAdmin: boolean;
  readonly isSessionServerAdmin: boolean;

  readonly doesTheServerHaveMoreThanOneSite: boolean;
  readonly isSiteAdminSettingsEnabled: boolean;
  readonly isAdminViewsEnabled: boolean;

  readonly isCatalogingOnSiteEnabled: boolean;
  readonly isNamedSharingEnabled: boolean;
  readonly isVizRecommendationsEnabled: boolean;
  readonly shouldShowPersonalSpace: boolean;
  readonly isSitePickerEnabled: boolean;
  readonly isCustomDomainEnabled: boolean;
  readonly isSchedulesPageHidden: boolean;
  readonly isGroupSetsEnabled: boolean;
  readonly siteCountAndCloudAdminStatus: ISiteCountAndCloudAdminStatus;
  readonly canPublish: boolean;
}

export interface NavPanelDispatchProps {
  requestSiteCountAndCloudAdminStatus: () => void;
}

export type NavPanelProps = NavPanelOwnProps & NavPanelStateProps & NavPanelDispatchProps;

export const LogoWrapperDivTestId = 'logo-div-wrapper';
export class NavPanel extends React.PureComponent<NavPanelProps, { readyToRenderLogo: boolean }> {
  constructor(props: NavPanelProps) {
    super(props);
    this.state = { readyToRenderLogo: false };
  }

  public componentDidMount(): void {
    ServerSettingsUnauthenticated.fetchServerSettingsUnauthenticatedPromise.then(() => {
      // Only when the promise above is resolved, the staticUrlPrefix could be ready for the images when it's in the online environment.
      this.setState({ readyToRenderLogo: true });
    });
  }

  public render(): JSX.Element {
    return (
      <nav data-tb-test-id="nav-panel" aria-label={Messages.navigationPanelTitle()}>
        {this.state.readyToRenderLogo && this.renderLogo()}
        {this.shouldShowSitePicker() && this.renderSitePicker()}
        {this.shouldShowLinkEnabledSitePicker() && this.renderLinkEnabledSitePicker()}
        {this.navList()}
      </nav>
    );
  }

  private navList(): JSX.Element {
    const items: NavListItem[] = this.shouldShowServerLinks() ? this.serverAdminLinks() : this.siteLinks();
    if(isRecycleBinEnabled() && this.props.canPublish && !this.props.inServerArea) {
      items.push(...this.getRecycleBinLink());
    }
    const props: NavListProps = {
      items: items,
      selectedValue: this.props.currentNavLocation,
      onSelect: this.onNavListSelect
    };

    return <NavList  {...props} />;
  }

  private onNavListSelect = (value: string) => {
    Telemetry.sendEvent<LeftNavLinkNavigationEvent>({
      type: TelemetryEvents.LeftNavLinkNavigation,
      navLocation: value,
      previousSpace: this.props.spaceType,
    });
    this.onSelection();
  };

  private siteLinks(): NavListItem[] {
    const items = this.getSiteContentLinks();
    if (this.isSessionSiteOrServerAdmin()) {
      return items.concat(this.siteAdminLinks());
    }
    return items;
  }

  private getSiteContentLinks(): NavListItem[] {
    return this.siteContentLinks();
  }

  private getRecycleBinLink(): NavListItem[] {
    return _.compact<_.List<NavListItem | false>>([
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      }, {
        content: ContentLinks.RecycleBinNavListLink(this.props),
        value: NavLocationAreas.SiteContent.recyclebin
        }
      ]);
  }

  private siteContentLinks(): NavListItem[] {
    return _.compact<_.List<NavListItem | false>>([
      {
        content: ContentLinks.HomeNavListLink(this.props),
        value: NavLocationAreas.SiteContent.home
      },
      {
        content: ContentLinks.FavoritesNavListLink(this.props),
        value: NavLocationAreas.SiteContent.favorites
      },
      {
        content: ContentLinks.RecentsNavListLink(this.props),
        value: NavLocationAreas.SiteContent.recents
      },
      this.props.isNamedSharingEnabled && {
        content: ContentLinks.SharedWithMeNavListLink(this.props),
        value: NavLocationAreas.SiteContent.shared_with_me
      },
      this.props.isVizRecommendationsEnabled && {
        content: ContentLinks.RecommendationsNavListLink(this.props),
        value: NavLocationAreas.SiteContent.recommendations
      },
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      },
      this.props.shouldShowPersonalSpace && {
        content: ContentLinks.PersonalSpaceNavListLink(this.props),
        value: NavLocationAreas.SiteContent.personal_space
      },
      CollectionUtils.checkCollectionSiteSetting() && {
        content: ContentLinks.CollectionsNavListLink(this.props),
        value: NavLocationAreas.SiteContent.collections
      },
      {
        content: ContentLinks.ExploreNavListLink(this.props),
        value: NavLocationAreas.SiteContent.explore
      },
      Utils.isPulseUser() && {
        content: ContentLinks.PulseNavListLink(this.props),
        value: NavLocationAreas.SiteContent.metrics
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      },
      this.props.isCatalogingOnSiteEnabled && {
        content: ContentLinks.CatalogNavListLink(this.props),
        value: NavLocationAreas.SiteContent.catalog
      }
    ]);
  }

  private separatorNavListLink = (navPanelDisplayMode: NavPanelDisplayMode): NavListItemRenderProp => {
    return function(navListItemState: NavListItemState): JSX.Element {
      return <NavSectionSeparator navPanelDisplayMode={navPanelDisplayMode} />;
    };
  };

  private siteAdminLinks(): NavListItem[] {
    const showDataLabelsTab = (this.props.isCatalogingOnSiteEnabled && FeatureFlags.labelManagementEnabled)
      || showDMAwareness(true);
    return _.compact<_.List<NavListItem | false>>([
      {
        content: this.separatorNavListLink(this.props.navPanelDisplayMode),
        value: '',
      },
      {
        content: SiteLinks.UsersNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_users
      },
      {
        content: SiteLinks.GroupsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_groups
      },
      FeatureFlags.permissionsGroupLogicalAndEnabled && this.props.isGroupSetsEnabled && {
        content: SiteLinks.GroupSetsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_group_sets
      },
      !this.props.isSchedulesPageHidden && {
        content: SiteLinks.SchedulesNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_schedules
      },
      FeatureFlags.jobManagementEnabled && {
        content: SiteLinks.JobsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_jobs
      },
      {
        content: SiteLinks.TasksNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_tasks
      },
      showDataLabelsTab &&
      {
        content: SiteLinks.LabelsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_labels
      },
      this.statusLinkForSitePages(),
      this.settingLinkForSitePages()
    ]);
  }

  private serverAdminLinks(): NavListItem[] {
    return _.compact<_.List<NavListItem | false>>([
      {
        content: ServerLinks.SitesNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_sites
      },
      {
        content: ServerLinks.UsersNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_users
      },
      {
        content: ServerLinks.SchedulesNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_schedules
      },
      FeatureFlags.jobManagementEnabled && {
        content: ServerLinks.JobsNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_jobs
      },
      {
        content: ServerLinks.TasksNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_tasks
      },
      {
        content: ServerLinks.StatusNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_status
      },
      {
        content: ServerLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_settings
      }
    ]);
  }

  private renderSitePicker() {
    return (
      <ConnectedSitePicker
        navPanelDisplayMode={this.props.navPanelDisplayMode}
        serverUrls={this.props.serverUrls}
        onSitePickerClick={this.props.onSitePickerClick}
        onSelection={this.onSelection}
      />
    );
  }

  private renderLinkEnabledSitePicker() {
    return (
      <ConnectedLinkEnabledSitePicker
        navPanelDisplayMode={this.props.navPanelDisplayMode}
        onSitePickerClick={this.props.onSitePickerClick}
        onSelection={this.onSelection}
      />
    );
  }

  private shouldShowServerLinks() {
    return this.props.inServerArea && this.props.doesTheServerHaveMoreThanOneSite;
  }

  private isSessionSiteOrServerAdmin(): boolean {
    return this.props.isSessionSiteAdmin || this.props.isSessionServerAdmin;
  }

  private getCorrectSettingsLinkToShowOnSiteForServerAdmin(): NavListItem {
    if (this.props.doesTheServerHaveMoreThanOneSite) {
      return {
        content: SiteLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_settings
      };
    } else {
      // server admins on a server with only one site, get the server settings page
      return {
        content: ServerLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_settings
      };
    }
  }

  private settingLinkForSitePages(): NavListItem {
    if (this.props.isSessionServerAdmin) {
      return this.getCorrectSettingsLinkToShowOnSiteForServerAdmin();
    } else if (this.props.isSessionSiteAdmin && this.props.isSiteAdminSettingsEnabled) {
      return {
        content: SiteLinks.SettingsNavListLink(this.props),
        value: NavLocationAreas.SiteAdmin.site_settings
      };
    }

    return undefined;
  }

  private statusLinkForSitePages(): NavListItem {
    if (this.props.doesTheServerHaveMoreThanOneSite || this.props.isSessionSiteAdmin) {
      if (this.props.isAdminViewsEnabled) {
        return {
          content: SiteLinks.StatusNavListLink(this.props),
          value: NavLocationAreas.SiteAdmin.site_status
        };
      } else {
        return undefined;
      }
    } else { // server admin on server with one site
      return {
        content: ServerLinks.StatusNavListLink(this.props),
        value: NavLocationAreas.ServerAdmin.server_status
      };
    }
  }

  private shouldShowSitePicker(): boolean {
    return !FeatureFlags.syncToLinkEnabled &&
      this.props.siteCountAndCloudAdminStatus.numberOfSites > 1 && this.props.isSitePickerEnabled &&
      !this.props.isCustomDomainEnabled;
  }

  private shouldShowLinkEnabledSitePicker(): boolean {
    return FeatureFlags.syncToLinkEnabled &&
      this.props.isSitePickerEnabled &&
      !this.props.isCustomDomainEnabled &&
      (this.props.siteCountAndCloudAdminStatus.numberOfSites > 1 || this.props.siteCountAndCloudAdminStatus.isCloudAdmin);
  }

  private renderLogo(): JSX.Element {
    const logo: ILogo = LogoService.getNavPanelLogo();
    const compactLogo: ILogo = LogoService.getCompactLogo();
    const collapsed = this.props.navPanelDisplayMode === 'icon';

    return <NavPanelLogo logo={logo} compactLogo={compactLogo} siteUrls={this.props.siteUrls} collapsed={collapsed} />;
  }

  private onSelection = () => {
    if (this.props.onSelectionMade) {
      this.props.onSelectionMade();
    }
  };
}
